import {
    type OperationTimestampOptions,
    toOperationTimestamp,
} from '@/components/organisms/FishGroupForms';
import type { FishGroupOperationRequest } from '@/domain/fishGroups';

import type { OperateWizardState } from './types';

/**
 * Serialise wizard state into the request body expected by `POST /operate`.
 * String form fields (estimatedFishCount) are coerced to numbers; empty
 * strings become `undefined` (omitted from the payload) so the backend
 * treats them as "not provided".
 *
 * `operationDate` is converted from the picked calendar date to a timestamp
 * the backend's date guard accepts — see `toOperationTimestamp`. Doing it here
 * rather than at pick time means a wizard left open across midnight still
 * resolves "today" correctly. Callers should pass `notBefore` (the latest
 * source location start) so an operation on that same opening day is not
 * rejected; `reference` is injectable for tests.
 */
export function buildOperationRequest(
    state: OperateWizardState,
    timestampOptions: OperationTimestampOptions = {}
): FishGroupOperationRequest {
    return {
        sources: state.sources.map((s) => ({ fishGroupId: s.fishGroupId })),
        destinations: state.destinations.map((d) => ({
            name: d.name.trim(),
            cageId: d.cageId as number, // guarded by validateDestinations before this point
            estimatedFishCount: parseOptionalCount(d.estimatedFishCount),
            receivesFrom: d.receivesFrom.map((i) => ({
                sourceFishGroupId: i.sourceFishGroupId,
                estimatedFishCount: parseOptionalCount(i.estimatedFishCount),
            })),
            notes: d.notes.trim() || undefined,
        })),
        operationDate: state.operationDate
            ? toOperationTimestamp(new Date(state.operationDate), timestampOptions)
            : undefined,
        reason: state.reason.trim() || undefined,
        notes: state.notes.trim() || undefined,
        ...(state.bioscopeMovements.length > 0
            ? {
                  bioscopeMovements: state.bioscopeMovements.map((m) => ({
                      bioscopeId: m.bioscopeId,
                      targetCageId: m.targetCageId,
                  })),
              }
            : {}),
    };
}

function parseOptionalCount(value: string): number | undefined {
    const trimmed = value.trim();
    if (trimmed === '') return undefined;
    const n = Number(trimmed);
    return Number.isFinite(n) && n > 0 ? n : undefined;
}
